import React, { useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";

import { submitBulkProposalWorkbook } from "../../../services/documentGeneration.service";
import { BulkSubmitResponse } from "../../../types/proposalDocuments.types";

import { describeHttpError, readJsonSafely } from "./bulk.utils";

const BulkWorkbookUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [result, setResult] = useState<BulkSubmitResponse | null>(null);
  // Bumped to clear the file input after a successful submit
  const [inputKey, setInputKey] = useState(0);

  const handleSubmit = async () => {
    if (!file) return;

    setIsSubmitting(true);
    setErrorMessage("");
    setResult(null);
    try {
      const res = await submitBulkProposalWorkbook(file);
      const data = await readJsonSafely(res);
      if (!res.ok) {
        setErrorMessage(describeHttpError(res.status, data));
        return;
      }

      const response = data as BulkSubmitResponse;
      setResult(response);
      if (response.ok) {
        setFile(null);
        setInputKey((k) => k + 1);
      }
    } catch {
      setErrorMessage("An unexpected error occurred. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <p className="text-muted">
        Upload a completed bulk proposal workbook to validate it and submit the documents.
      </p>
      <div className="d-flex gap-2 align-items-start mb-3">
        <Form.Control
          key={inputKey}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          aria-label="Completed bulk proposal workbook"
          disabled={isSubmitting}
          onChange={(e) => {
            setFile((e.target as HTMLInputElement).files?.[0] ?? null);
            setResult(null);
            setErrorMessage("");
          }}
        />
        <Button
          variant="primary"
          className="text-nowrap"
          disabled={!file || isSubmitting}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting ? "Submitting..." : "Submit workbook"}
        </Button>
      </div>

      {errorMessage && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => {
            setErrorMessage("");
          }}
        >
          {errorMessage}
        </Alert>
      )}

      {result && !result.ok && (
        <>
          <Alert variant="danger">
            Please fix these {result.errors.length} error{result.errors.length === 1 ? "" : "s"} in
            the workbook and upload it again - nothing has been submitted.
          </Alert>
          <Table size="sm" bordered responsive>
            <thead>
              <tr>
                <th>Sheet</th>
                <th>Row</th>
                <th>Zoho ID</th>
                <th>Meter ID</th>
                <th>Problem</th>
              </tr>
            </thead>
            <tbody>
              {result.errors.map((err, i) => (
                <tr key={`${err.sheet}-${String(err.row)}-${String(i)}`}>
                  <td>{err.sheet}</td>
                  <td>{err.row}</td>
                  <td>{err.zoho_id ?? ""}</td>
                  <td>{err.zoho_meter_id ?? ""}</td>
                  <td style={{ whiteSpace: "pre-wrap" }}>{err.message}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {result?.ok && (
        <Alert variant="success">
          <div>
            {result.customers.length} job{result.customers.length === 1 ? "" : "s"} submitted
            successfully.
          </div>
          <ul className="mb-0">
            {result.customers.map((c) => (
              <li key={c.zoho_id}>
                {c.customer_name ?? c.zoho_id} - {c.offer_count} offer
                {c.offer_count === 1 ? "" : "s"}, {c.quote_count} quote
                {c.quote_count === 1 ? "" : "s"}
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </div>
  );
};

export default BulkWorkbookUpload;
